import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Match,
  Bet,
  BetStatus,
  UserTier,
  ROUTE_PATHS,
  toMYT,
} from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { useBetting } from '@/hooks/useBetting';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useLanguage } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, Filter, ChevronRight, ChevronDown, BookOpen, Check, X, Receipt, Download, Send, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

/** Void window: 5 minutes in ms */
const VOID_WINDOW_MS = 5 * 60 * 1000;

/** Returns raw status key for a bet */
const getBetStatusKey = (matchStartTime: string, betStatus: string): 'Pending' | 'Closed' | 'Cancelled' => {
  if (betStatus === 'CANCELLED') return 'Cancelled';
  const start = new Date(matchStartTime).getTime();
  const now = Date.now();
  return now < start - 3 * 60 * 1000 ? 'Pending' : 'Closed';
};

/** Format timestamp → DD/MM/YY HH:MM:SS */
const fmtTs = (ts: string | undefined): string => {
  if (!ts) return '-';
  try {
    const d = toMYT(ts);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear().toString().slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch { return '-'; }
};

/** Recursively collect all descendant user IDs of a given user */
const getDescendantIds = (userId: string, allUsers: User[]): string[] => {
  const result: string[] = [];
  const recurse = (id: string) => {
    const children = allUsers.filter(u => u.parentId === id);
    children.forEach(c => { result.push(c.id); recurse(c.id); });
  };
  recurse(userId);
  return result;
};

/** Countdown component — shows remaining seconds, calls onExpire when done */
function VoidCountdown({ createdAt, onExpire }: { createdAt: string; onExpire: () => void }) {
  const calcRemaining = () => {
    const elapsed = Date.now() - new Date(createdAt).getTime();
    return Math.max(0, Math.ceil((VOID_WINDOW_MS - elapsed) / 1000));
  };
  const [remaining, setRemaining] = useState(calcRemaining);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (remaining <= 0) { onExpireRef.current(); return; }
    const id = setInterval(() => {
      const r = calcRemaining();
      setRemaining(r);
      if (r <= 0) { clearInterval(id); onExpireRef.current(); }
    }, 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const urgent = remaining <= 60;

  return (
    <span className={`font-mono text-[10px] tabular-nums ${urgent ? 'text-red-500' : 'text-muted-foreground'}`}>
      {pad(mins)}:{pad(secs)}
    </span>
  );
}

/** Password confirmation dialog overlay */
function PasswordConfirmDialog({
  onConfirm,
  onCancel,
  loading,
}: {
  onConfirm: (password: string) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) { setError('Please enter your password.'); return; }
    setError('');
    onConfirm(password);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-background rounded-xl shadow-2xl border border-border w-80 p-6 space-y-4">
        <h2 className="text-base font-semibold">{t.reports.confirmVoid}</h2>
        <p className="text-sm text-muted-foreground">{t.reports.confirmVoidDesc}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            ref={inputRef}
            type="password"
            placeholder={t.reports.enterPasswordPlaceholder}
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            className="h-9"
            disabled={loading}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              disabled={loading}
            >
              {loading ? t.reports.voiding : t.reports.confirmVoidBtn}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={loading}
            >
              {t.reports.cancel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Country code map (for flag display in receipt) ─────────────────────────
const RECEIPT_COUNTRY_CODES: Record<string, string> = {
  'Mexico': 'MX', 'South Africa': 'ZA', 'South Korea': 'KR', 'Korea Republic': 'KR',
  'Czechia': 'CZ', 'Czech Republic': 'CZ', 'Canada': 'CA', 'Bosnia and Herzegovina': 'BA',
  'Qatar': 'QA', 'Switzerland': 'CH', 'Brazil': 'BR', 'Morocco': 'MA', 'Haiti': 'HT',
  'Scotland': 'GB-SCT', 'United States': 'US', 'USA': 'US', 'Paraguay': 'PY',
  'Australia': 'AU', 'Türkiye': 'TR', 'Turkey': 'TR', 'Germany': 'DE', 'Curaçao': 'CW',
  'Ivory Coast': 'CI', "Côte d'Ivoire": 'CI', 'Ecuador': 'EC', 'Netherlands': 'NL',
  'Japan': 'JP', 'Egypt': 'EG', 'Sweden': 'SE', 'Belgium': 'BE', 'Iran': 'IR',
  'New Zealand': 'NZ', 'Tunisia': 'TN', 'Spain': 'ES', 'Cape Verde': 'CV',
  'Cabo Verde': 'CV', 'Saudi Arabia': 'SA', 'Uruguay': 'UY', 'France': 'FR',
  'Senegal': 'SN', 'Iraq': 'IQ', 'Austria': 'AT', 'Argentina': 'AR', 'Algeria': 'DZ',
  'Norway': 'NO', 'Jordan': 'JO', 'Portugal': 'PT', 'Congo DR': 'CD', 'DR Congo': 'CD',
  'Uzbekistan': 'UZ', 'Colombia': 'CO', 'England': 'GB-ENG', 'Croatia': 'HR',
  'Ghana': 'GH', 'Panama': 'PA', 'Korea DPR': 'KP', 'North Korea': 'KP',
  'China': 'CN', 'China PR': 'CN', 'Costa Rica': 'CR', 'Honduras': 'HN',
  'El Salvador': 'SV', 'Jamaica': 'JM', 'Venezuela': 'VE', 'Chile': 'CL',
  'Peru': 'PE', 'Bolivia': 'BO', 'Serbia': 'RS', 'Ukraine': 'UA', 'Romania': 'RO',
  'Denmark': 'DK', 'Poland': 'PL', 'Wales': 'GB-WLS', 'Greece': 'GR',
  'Slovenia': 'SI', 'Slovakia': 'SK', 'Hungary': 'HU', 'Iceland': 'IS',
  'Finland': 'FI', 'Russia': 'RU', 'Cameroon': 'CM', 'Nigeria': 'NG', 'Mali': 'ML',
  'Guinea': 'GN', 'Tanzania': 'TZ', 'Zambia': 'ZM', 'Angola': 'AO', 'Kenya': 'KE',
  'Mozambique': 'MZ', 'Uganda': 'UG', 'Libya': 'LY', 'Sudan': 'SD', 'Ethiopia': 'ET',
  'United Arab Emirates': 'AE', 'UAE': 'AE', 'Kuwait': 'KW', 'Bahrain': 'BH',
  'Oman': 'OM', 'Syria': 'SY', 'Lebanon': 'LB', 'Palestine': 'PS', 'Thailand': 'TH',
  'Vietnam': 'VN', 'Indonesia': 'ID', 'Philippines': 'PH', 'India': 'IN',
  'Malaysia': 'MY', 'Singapore': 'SG',
};

function isoToFlagCdnR(code: string): string {
  if (code.startsWith('GB-')) return 'gb';
  return code.toLowerCase();
}

/** Round country flag component for receipt */
function ReceiptCountryFlag({ country, size = 52 }: { country: string; size?: number }) {
  const code = RECEIPT_COUNTRY_CODES[country];
  const [err, setErr] = React.useState(false);
  const style: React.CSSProperties = {
    width: size, height: size, minWidth: size, borderRadius: '50%',
    overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid rgba(0,0,0,0.12)',
  };
  if (!code || err) {
    return <div style={{ ...style, background: '#e5e7eb', fontSize: Math.round(size * 0.55) }}><span>🌍</span></div>;
  }
  const src = `https://flagcdn.com/256x192/${isoToFlagCdnR(code)}.png`;
  return (
    <div style={style}>
      <img src={src} alt={country} onError={() => setErr(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
    </div>
  );
}

/** Helper: convert country name → emoji flag using Unicode regional indicators */
function countryToEmoji(country: string): string {
  const code = RECEIPT_COUNTRY_CODES[country];
  if (!code) return '🌍';
  const base = code.startsWith('GB-') ? 'GB' : code.toUpperCase();
  const offset = 0x1F1E6 - 65;
  return String.fromCodePoint(offset + base.charCodeAt(0), offset + base.charCodeAt(1));
}

/** Canvas rounded rect helper */
function rRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

/** Generate receipt canvas — same style as BetReceipt in MatchBetting */
function generateViewReceiptCanvas(
  transactionId: string,
  username: string,
  matchObj: Match | undefined,
  bets: Array<{ selection: string; oddsAtTime: number; amount: number; status: string; updatedAt?: string }>,
  remark?: string
): string {
  const isCancelled = bets.some(b => b.status === 'CANCELLED');
  const cancelledAt = isCancelled ? (bets.find(b => b.status === 'CANCELLED')?.updatedAt ?? null) : null;
  const cancelledAtStr = cancelledAt ? format(toMYT(cancelledAt), 'yyyy-MM-dd HH:mm:ss') + ' MYT' : '';
  const totalBet = isCancelled ? 0 : bets.reduce((s, b) => s + b.amount, 0);
  const SCALE = 2, W = 600, ROW_H = 32, PAD = 20;
  const INFO_H = 160, MATCH_H = 140, SBETS_H = 40 + (bets.length + 1) * ROW_H + 48;
  const HEIGHT = INFO_H + MATCH_H + SBETS_H + PAD;
  const canvas = document.createElement('canvas');
  canvas.width = W * SCALE; canvas.height = HEIGHT * SCALE;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, HEIGHT);
  let y = 0;

  // Dark header
  ctx.fillStyle = '#1a2744'; ctx.fillRect(0, y, W, 46);
  ctx.fillStyle = '#ffffff'; ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'left'; ctx.fillText('TRANSACTION RECEIPT', PAD + 28, y + 28);
  y += 46;

  // Extract date/time from transactionId
  const dd = transactionId.slice(0, 2), mm2 = transactionId.slice(2, 4), yy = transactionId.slice(4, 6);
  const hh = transactionId.slice(6, 8), mn = transactionId.slice(8, 10), ss2 = transactionId.slice(10, 12);
  // Transaction ID encodes UTC time — convert to MYT for canvas
  const txUtcDateC = new Date(`20${yy}-${mm2}-${dd}T${hh}:${mn}:${ss2}Z`);
  const txMYTC = toMYT(txUtcDateC.toISOString());
  const dtStr = format(txMYTC, 'yyyy-MM-dd HH:mm:ss') + ' MYT';

  const infoRows: [string, string][] = [
    ['SERIAL', transactionId],
    ['STATUS', isCancelled ? '⚠ Cancelled' : '✓ Success'],  // pill text only
    ['DATE TIME', dtStr],
    ['NICKNAME', username],
    ['REMARK', remark || ''],
  ];
  for (const [label, val] of infoRows) {
    ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    ctx.fillStyle = '#374151'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'left'; ctx.fillText(label, PAD, y + 20);
    if (label === 'STATUS') {
      if (isCancelled) {
        // Red pill with 'Cancelled'
        ctx.fillStyle = '#ef4444';
        rRect(ctx, 140, y + 8, 92, 22, 11);
        ctx.fillStyle = '#ffffff'; ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'left'; ctx.fillText('⚠ Cancelled', 152, y + 22);
        // Cancellation datetime to the right of the pill
        if (cancelledAtStr) {
          ctx.fillStyle = '#6b7280'; ctx.font = '10px Arial';
          ctx.textAlign = 'left'; ctx.fillText(cancelledAtStr, 244, y + 22);
        }
      } else {
        ctx.fillStyle = '#22c55e';
        rRect(ctx, 140, y + 8, 90, 22, 11);
        ctx.fillStyle = '#ffffff'; ctx.font = '11px Arial';
        ctx.textAlign = 'left'; ctx.fillText('✓ Success', 152, y + 22);
      }
    } else {
      ctx.fillStyle = '#6b7280'; ctx.font = '11px Arial';
      ctx.textAlign = 'left'; ctx.fillText(val, 140, y + 20);
    }
    y += 22;
  }

  // Match banner
  const grad = ctx.createLinearGradient(0, y, W, y + MATCH_H);
  grad.addColorStop(0, '#1a2744'); grad.addColorStop(1, '#2d3e6e');
  ctx.fillStyle = grad; ctx.fillRect(0, y, W, MATCH_H);
  if (matchObj) {
    // HOME badge
    ctx.fillStyle = '#3b82f6'; rRect(ctx, PAD, y + 10, 52, 18, 9);
    ctx.fillStyle = '#fff'; ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center'; ctx.fillText('HOME', PAD + 26, y + 22);
    // AWAY badge
    ctx.fillStyle = '#ef4444'; rRect(ctx, W - PAD - 52, y + 10, 52, 18, 9);
    ctx.fillStyle = '#fff'; ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center'; ctx.fillText('AWAY', W - PAD - 26, y + 22);
    // Emoji flags
    const flagY2 = y + 40, flagR = 32;
    const homeCX = W * 0.22, awayCX = W * 0.78;
    const homeEmoji = countryToEmoji(matchObj.homeTeam);
    const awayEmoji = countryToEmoji(matchObj.awayTeam);
    ctx.font = `${flagR * 1.5}px Arial`; ctx.textAlign = 'center';
    ctx.fillText(homeEmoji, homeCX, flagY2 + flagR + 10);
    ctx.fillText(awayEmoji, awayCX, flagY2 + flagR + 10);
    // Team names
    ctx.fillStyle = '#ffffff'; ctx.font = 'bold 13px Arial'; ctx.textAlign = 'center';
    ctx.fillText(matchObj.homeTeam, homeCX, flagY2 + flagR + 32);
    ctx.fillText(matchObj.awayTeam, awayCX, flagY2 + flagR + 32);
    // VS circle
    ctx.beginPath(); ctx.arc(W / 2, flagY2, 22, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.3)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#ffffff'; ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center'; ctx.fillText('VS', W / 2, flagY2 + 4);
    // Match date
    try {
      const mdStr = format(toMYT(matchObj.startTime), 'yyyy-MM-dd HH:mm') + ' MYT';
      ctx.fillStyle = 'rgba(255,255,255,0.75)'; ctx.font = '9px Arial';
      ctx.textAlign = 'center'; ctx.fillText(mdStr, W / 2, flagY2 + 44);
    } catch { /* ignore */ }
  } else {
    ctx.fillStyle = '#ffffff'; ctx.font = 'bold 13px Arial';
    ctx.textAlign = 'center'; ctx.fillText('Match Details Unavailable', W / 2, y + MATCH_H / 2);
  }
  // Rainbow stripe
  const rainbow = ctx.createLinearGradient(0, 0, W, 0);
  rainbow.addColorStop(0, '#ef4444'); rainbow.addColorStop(0.2, '#f97316');
  rainbow.addColorStop(0.4, '#eab308'); rainbow.addColorStop(0.6, '#22c55e');
  rainbow.addColorStop(0.8, '#3b82f6'); rainbow.addColorStop(1, '#8b5cf6');
  ctx.fillStyle = rainbow; ctx.fillRect(0, y + MATCH_H - 6, W, 6);
  y += MATCH_H;

  // SCORE BETS section header
  ctx.fillStyle = '#3b50a0'; ctx.fillRect(0, y, W, 34);
  ctx.fillStyle = '#ffffff'; ctx.font = 'bold 12px Arial';
  ctx.textAlign = 'left'; ctx.fillText('🎯  SCORE BETS', PAD, y + 22);
  y += 34;

  // Table header
  ctx.fillStyle = '#f3f4f6'; ctx.fillRect(0, y, W, ROW_H);
  ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, y + ROW_H); ctx.lineTo(W, y + ROW_H); ctx.stroke();
  const COL = [PAD, W * 0.4, W * 0.6, W - PAD];
  ctx.fillStyle = '#374151'; ctx.font = 'bold 10px Arial';
  ctx.textAlign = 'left'; ctx.fillText('SCORE', COL[0], y + 20);
  ctx.textAlign = 'center'; ctx.fillText('ODD', (COL[1] + COL[2]) / 2, y + 20);
  ctx.textAlign = 'right'; ctx.fillText('BET', COL[3], y + 20);
  y += ROW_H;

  // Table rows
  for (let i = 0; i < bets.length; i++) {
    const b = bets[i];
    const bc = b.status === 'CANCELLED';
    ctx.fillStyle = i % 2 === 0 ? '#ffffff' : '#f9fafb';
    ctx.fillRect(0, y, W, ROW_H);
    ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(0, y + ROW_H); ctx.lineTo(W, y + ROW_H); ctx.stroke();
    ctx.fillStyle = bc ? '#9ca3af' : '#111827';
    ctx.font = bc ? '11px Arial' : 'bold 11px Arial'; ctx.textAlign = 'left';
    ctx.fillText(bc ? `${b.selection} (Cancelled)` : b.selection, COL[0], y + 20);
    ctx.fillStyle = bc ? '#9ca3af' : '#3b82f6'; ctx.font = '11px Arial';
    ctx.textAlign = 'center'; ctx.fillText(b.oddsAtTime.toFixed(2), (COL[1] + COL[2]) / 2, y + 20);
    ctx.fillStyle = bc ? '#9ca3af' : '#374151'; ctx.textAlign = 'right';
    ctx.fillText(bc ? 'Cancelled' : b.amount.toFixed(2), COL[3], y + 20);
    y += ROW_H;
  }

  // Total row
  ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  y += 4;
  ctx.fillStyle = '#111827'; ctx.font = 'bold 13px Arial';
  ctx.textAlign = 'left'; ctx.fillText('🪙  Total Bet', PAD, y + 28);
  if (isCancelled) {
    ctx.fillStyle = '#ef4444'; ctx.font = 'bold 13px Arial';
    ctx.textAlign = 'right'; ctx.fillText('Cancelled', W - PAD, y + 28);
  } else {
    ctx.fillStyle = '#22c55e'; ctx.font = 'bold 13px Arial';
    ctx.textAlign = 'right'; ctx.fillText(totalBet.toFixed(2), W - PAD, y + 28);
  }
  return canvas.toDataURL('image/png');
}

/** ========= VIEW RECEIPT MODAL ========= */
interface SoldOutItem {
  selection: string;
  soldOutAmount: number;
  requestedAmount: number;
  acceptedAmount: number;
  tightestTier: string;
}

interface ReceiptBet {
  selection: string;
  oddsAtTime: number;
  amount: number;
  status: string;
  transactionId?: string;
  createdAt?: string;
  updatedAt?: string;  // cancellation/void timestamp
  remark?: string;
  soldOutAmount?: number;
}

function ViewReceiptModal({
  transactionId,
  bets,
  matchObj,
  username,
  remark,
  soldOutItems,
  onClose,
}: {
  transactionId: string;
  bets: ReceiptBet[];
  matchObj: Match | undefined;
  username: string;
  remark?: string;
  soldOutItems?: SoldOutItem[];
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const isCancelled = bets.some(b => b.status === 'CANCELLED');
  const cancelledAt = isCancelled ? (bets.find(b => b.status === 'CANCELLED')?.updatedAt ?? null) : null;
  const cancelledAtStr = cancelledAt ? format(toMYT(cancelledAt), 'yyyy-MM-dd HH:mm:ss') + ' MYT' : '';
  const totalBet = isCancelled ? 0 : bets.reduce((s, b) => s + b.amount, 0);
  const [imgDataUrl, setImgDataUrl] = React.useState<string | null>(null);
  const [imgLoading, setImgLoading] = React.useState(true);
  const [sharing, setSharing] = React.useState(false);
  const [textSharing, setTextSharing] = React.useState(false);

  React.useEffect(() => {
    setImgLoading(true);
    try {
      const url = generateViewReceiptCanvas(transactionId, username, matchObj, bets, remark);
      setImgDataUrl(url);
    } catch { /* silent */ } finally {
      setImgLoading(false);
    }
  }, [transactionId, username, matchObj, bets, remark]);

  const dd = transactionId.slice(0, 2), mm2 = transactionId.slice(2, 4), yy = transactionId.slice(4, 6);
  const hh = transactionId.slice(6, 8), mn = transactionId.slice(8, 10), ss2 = transactionId.slice(10, 12);
  // Transaction ID encodes UTC time — convert to MYT for display
  const txUtcDate = new Date(`20${yy}-${mm2}-${dd}T${hh}:${mn}:${ss2}Z`);
  const txMYT = toMYT(txUtcDate.toISOString());
  const dtStr = format(txMYT, 'yyyy-MM-dd HH:mm:ss') + ' MYT';
  const matchDate = matchObj ? format(toMYT(matchObj.startTime), 'yyyy-MM-dd HH:mm:ss') + ' MYT' : '';

  const handleShare = async () => {
    if (!imgDataUrl) return;
    setSharing(true);
    try {
      const blob = await (await fetch(imgDataUrl)).blob();
      const file = new File([blob], `receipt_${transactionId}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: 'Bet Receipt', text: `Receipt: ${transactionId}`, files: [file] });
      } else {
        const a = document.createElement('a'); a.href = imgDataUrl;
        a.download = `receipt_${transactionId}.png`; a.click();
      }
    } catch { /* cancelled */ } finally { setSharing(false); }
  };

  const handleSave = () => {
    if (!imgDataUrl) return;
    const a = document.createElement('a'); a.href = imgDataUrl;
    a.download = `receipt_${transactionId}.png`; a.click();
  };

  const handlePrint = () => {
    if (!imgDataUrl) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<html><head><title>Receipt ${transactionId}</title><style>body{margin:0;display:flex;justify-content:center;padding:16px}img{max-width:100%;height:auto}@media print{body{padding:0}}</style></head><body><img src="${imgDataUrl}" onload="window.print()"/></body></html>`);
    win.document.close();
  };

  // ── Text-only share ──
  const buildReceiptText = (): string => {
    const lines: string[] = [];
    lines.push('===== TRANSACTION RECEIPT =====');
    lines.push(`Serial   : ${transactionId}`);
    lines.push(`Status   : ${isCancelled ? 'CANCELLED' + (cancelledAtStr ? '  ' + cancelledAtStr : '') : 'CONFIRMED'}`);
    lines.push(`Date/Time: ${dtStr}`);
    lines.push(`Nickname : ${username}`);
    if (remark) lines.push(`Remark   : ${remark}`);
    if (matchObj) lines.push(`Match    : ${matchObj.homeTeam} vs ${matchObj.awayTeam}`);
    lines.push('--------------------------------');
    lines.push('Score       | Odds  | Bet');
    lines.push('--------------------------------');
    bets.forEach(b => {
      const score   = b.selection.padEnd(11);
      const odds    = b.oddsAtTime.toFixed(2).padStart(5);
      const amt     = b.status === 'CANCELLED' ? 'Cancelled'.padStart(9) : b.amount.toFixed(2).padStart(9);
      const soNote  = (b.soldOutAmount ?? 0) > 0 ? ` (+${b.soldOutAmount!.toFixed(2)} sold out)` : '';
      lines.push(`${score} | ${odds} | ${amt}${soNote}`);
    });
    lines.push('--------------------------------');
    lines.push(`Total Bet: ${isCancelled ? 'Cancelled' : totalBet.toFixed(2)}`);
    const sos = soldOutItems ?? [];
    if (sos.length > 0) {
      lines.push('================================');
      lines.push('⛔ Sold Out Notice:');
      sos.forEach(si => lines.push(`  ${si.selection}: ${si.soldOutAmount.toFixed(2)} sold out`));
    }
    lines.push('================================');
    return lines.join('\n');
  };

  const handleTextShare = async () => {
    setTextSharing(true);
    try {
      const text = buildReceiptText();
      if (navigator.share) {
        await navigator.share({ title: 'Bet Receipt', text });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Receipt copied to clipboard! Paste into WhatsApp / WeChat / Telegram.');
      }
    } catch { /* user cancelled */ } finally { setTextSharing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-4">

        {/* Header bar */}
        <div className="flex items-center gap-2 px-4 py-3" style={{ background: '#1a2744' }}>
          <div className="w-5 h-5 rounded border border-gray-400 flex items-center justify-center">
            <div className="w-3 h-0.5 bg-gray-300" />
          </div>
          <span className="text-white font-bold text-sm tracking-wider">TRANSACTION RECEIPT</span>
        </div>

        {/* Info rows */}
        <div className="divide-y divide-gray-100">
          {([
            ['SERIAL', transactionId],
            ['STATUS', null],
            ['DATE TIME', dtStr],
            ['NICKNAME', username],
            ['REMARK', remark || ''],
          ] as [string, string | null][]).map(([label, val]) => (
            <div key={label} className="flex items-center px-4 py-2.5">
              <span className="w-28 text-xs font-bold text-gray-700 shrink-0">{label}</span>
              {label === 'STATUS' ? (
                isCancelled ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#ef4444' }}>
                      ⚠ Cancelled
                    </span>
                    {cancelledAtStr && (
                      <span className="text-xs font-mono text-gray-500">{cancelledAtStr}</span>
                    )}
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#22c55e' }}>
                    ✓ Success
                  </span>
                )
              ) : (
                <span className="text-xs font-mono text-gray-500">{val}</span>
              )}
            </div>
          ))}
        </div>

        {/* Match card */}
        <div className="relative flex items-center justify-around py-6 px-4" style={{ background: 'linear-gradient(135deg, #1a2744 0%, #2d3e6e 100%)' }}>
          <div className="flex flex-col items-center gap-2">
            <span className="px-2 py-0.5 rounded text-white text-[10px] font-bold" style={{ background: '#3b82f6' }}>HOME</span>
            <ReceiptCountryFlag country={matchObj?.homeTeam ?? ''} size={52} />
            <span className="text-white font-bold text-sm text-center">{matchObj?.homeTeam ?? '—'}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full border-2 border-white/30 flex items-center justify-center">
              <span className="text-white text-xs font-bold">VS</span>
            </div>
            {matchDate && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.35)' }}>
                <span className="text-white text-[10px]">🕐</span>
                <span className="text-white text-[10px] font-mono">{matchDate}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col items-center gap-2">
            <span className="px-2 py-0.5 rounded text-white text-[10px] font-bold" style={{ background: '#ef4444' }}>AWAY</span>
            <ReceiptCountryFlag country={matchObj?.awayTeam ?? ''} size={52} />
            <span className="text-white font-bold text-sm text-center">{matchObj?.awayTeam ?? '—'}</span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1.5"
            style={{ background: 'linear-gradient(to right,#ef4444,#f97316,#eab308,#22c55e,#3b82f6,#8b5cf6)' }} />
        </div>

        {/* Score Bets */}
        <div>
          <div className="flex items-center gap-2 px-4 py-2" style={{ background: '#3b50a0' }}>
            <span className="text-lg">🎯</span>
            <span className="text-white font-bold text-xs tracking-widest">SCORE BETS</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="px-3 py-1.5 text-left font-bold text-gray-700 bg-gray-100">Score</th>
                  <th className="px-3 py-1.5 text-center font-bold text-gray-700 bg-gray-100">Odd</th>
                  <th className="px-3 py-1.5 text-right font-bold text-gray-700 bg-gray-100">Bet</th>
                </tr>
              </thead>
              <tbody>
                {bets.map((b, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className={`px-3 py-1.5 font-bold text-left ${b.status === 'CANCELLED' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                      {b.selection}
                    </td>
                    <td className={`px-3 py-1.5 text-center font-mono ${b.status === 'CANCELLED' ? 'text-gray-400' : 'text-blue-600'}`}>
                      {b.oddsAtTime.toFixed(2)}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono ${b.status === 'CANCELLED' ? 'text-red-400' : 'text-gray-700'}`}>
                      {b.status === 'CANCELLED' ? 'Cancelled' : (
                        <span>
                          {b.amount.toFixed(2)}
                          {(b.soldOutAmount ?? 0) > 0 && (
                            <span className="ml-1 text-[9px] text-red-500">(+{b.soldOutAmount!.toFixed(2)} sold out)</span>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Fully sold-out rows (acceptedAmount === 0) */}
                {(soldOutItems ?? []).filter(si => si.acceptedAmount <= 0).length > 0 && (
                  <tr className="border-t-2 border-red-200">
                    <td colSpan={3} className="px-3 py-1 text-[10px] font-semibold bg-red-50" style={{ color: '#dc2626' }}>⛔ {t.reports.soldOutLimit}</td>
                  </tr>
                )}
                {(soldOutItems ?? []).filter(si => si.acceptedAmount <= 0).map((si, i) => (
                  <tr key={`so${i}`} style={{ background: '#fef2f2' }}>
                    <td className="px-3 py-1.5 font-bold text-left" style={{ color: '#b91c1c' }}>{si.selection}</td>
                    <td className="px-3 py-1.5 text-center font-mono text-gray-400">—</td>
                    <td className="px-3 py-1.5 text-right font-mono line-through" style={{ color: '#ef4444' }}>{si.requestedAmount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm font-bold">🪙 Total Bet</span>
            <span className="text-sm font-bold" style={{ color: isCancelled ? '#ef4444' : '#22c55e' }}>
              {isCancelled ? 'Cancelled' : totalBet.toFixed(2)}
            </span>
          </div>
          {/* Sold-out notice banner */}
          {(soldOutItems ?? []).length > 0 && (
            <div className="mx-4 mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
              <span className="font-bold">⛔ {t.reports.soldOutNotice}:</span>{' '}
              {(soldOutItems ?? []).map((si, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  <span className="font-semibold">{si.selection}</span>:
                  {' '}{si.soldOutAmount.toFixed(2)} {t.reports.soldOut}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="px-4 pb-5 pt-3 space-y-2">
          {/* Row 1: Share Image / Save / Print */}
          <div className="grid grid-cols-3 gap-2">
            <button onClick={handleShare} disabled={imgLoading || sharing}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: '#25d366' }}>
              <Send className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{sharing ? t.reports.sharing : t.reports.shareImage}</span>
              <span className="sm:hidden">{sharing ? '…' : t.reports.shareImage}</span>
            </button>
            <button onClick={handleSave} disabled={imgLoading || !imgDataUrl}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: '#3b82f6' }}>
              <Download className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{t.reports.saveImage}</span>
              <span className="sm:hidden">{t.reports.saveImage}</span>
            </button>
            <button onClick={handlePrint} disabled={imgLoading || !imgDataUrl}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: '#6b7280' }}>
              <span className="text-base leading-none shrink-0">🖨️</span>
              <span className="hidden sm:inline">{t.reports.printReceipt}</span>
              <span className="sm:hidden">{t.reports.printReceipt}</span>
            </button>
          </div>
          {/* Row 2: Text Only Share */}
          <button
            onClick={handleTextShare}
            disabled={textSharing}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
            style={{ background: '#0088cc' }}
          >
            <span>💬</span>
            <span>{textSharing ? t.reports.sharing : t.reports.textShare}</span>
          </button>
          {imgLoading && <p className="text-center text-xs text-gray-400">{t.reports.preparingImage}</p>}
          {/* Row 3: Close */}
          <button onClick={onClose}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: '#1a2744' }}>
            {t.reports.closeReceipt}
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewReceiptModalWrapper(props: Parameters<typeof ViewReceiptModal>[0]) {
  return <ViewReceiptModal {...props} />;
}
// keep this export so HistoryReceiptPortal can use it without re-reading t

interface BetRow extends Bet {
  username: string;
  matchName: string;
  /** The viewing agent/company's CURRENT live fight% against direct child. */
  viewerFightPct?: number;
  /**
   * The fight% the viewer had applied against their direct child AT THE TIME the bet was placed.
   * Derived from fightPctSnapshotChain and fightPctSnapshot.
   * Used for timestamp-based grouping: bets with the same viewerFightPctAtTime were placed
   * under the same fight% period.
   */
  viewerFightPctAtTime?: number;
  /** The direct child of the viewer through whom this bet flows (used for grouping). */
  directChildOfViewerId?: string;
}

/** Helper component: renders ViewReceiptModal for the selected transaction — avoids IIFE-in-JSX TS errors */
function HistoryReceiptPortal({
  viewReceiptTxnId,
  allVisibleBets,
  effectiveBets,
  allMatches,
  onClose,
}: {
  viewReceiptTxnId: string | null;
  allVisibleBets: BetRow[];
  effectiveBets: BetRow[];
  allMatches: Match[];
  onClose: () => void;
}) {
  if (!viewReceiptTxnId) return null;
  const pool = allVisibleBets.length > 0 ? allVisibleBets : effectiveBets;
  const siblings = pool.filter(b => b.transactionId === viewReceiptTxnId);
  const refBet = siblings[0] || effectiveBets.find(b => b.transactionId === viewReceiptTxnId);
  if (!refBet) return null;
  const matchObj = allMatches.find(m => m.id === refBet.matchId);
  // Sort ascending so the first-inserted row (holding sold_out_snapshot) is first
  const sortedSibs = [...siblings].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  const rowWithSnap = sortedSibs.find(b => b.soldOutSnapshot && (b.soldOutSnapshot as SoldOutItem[]).length > 0);
  const soldOutSnap: SoldOutItem[] = (rowWithSnap?.soldOutSnapshot ?? []) as SoldOutItem[];
  const betsForModal = (sortedSibs.length > 0 ? sortedSibs : [refBet]).map(b => {
    const snap = soldOutSnap.find(
      s => s.selection === b.selection ||
        (b.selection === 'Any Other Score' && s.selection === 'AOS') ||
        (b.selection === 'AOS' && s.selection === 'Any Other Score')
    );
    return {
      selection: b.selection,
      oddsAtTime: b.oddsAtTime || 0,
      amount: b.amount,
      status: b.status,
      transactionId: b.transactionId,
      createdAt: b.createdAt,
      updatedAt: (b as any).updatedAt,
      remark: b.remark,
      soldOutAmount: snap ? snap.soldOutAmount : 0,
    };
  });
  return (
    <ViewReceiptModal
      transactionId={viewReceiptTxnId}
      bets={betsForModal}
      matchObj={matchObj}
      username={refBet.username}
      remark={refBet.remark}
      soldOutItems={soldOutSnap}
      onClose={onClose}
    />
  );
}

/** Props shared by bet-row renderers */
/** Props shared by bet-row renderers */
interface BetRowsSharedProps {
  allMatches: Match[];
  onCancelBet: (betId: string) => Promise<void>;
  currentUsername: string;
  /** current user's id — used for own-bet void check */
  currentUserId?: string;
  /** hide the Username column (player self-view) */
  hideUsernameCol: boolean;
  /** total number of columns in the table */
  totalCols: number;
  /** ALL visible bets (for receipt reconstruction) */
  allVisibleBets?: BetRow[];
  /** company-level viewer can void any downline bet at any time */
  isCompanyViewer?: boolean;
}

/** Renders the actual data rows for a list of bets, with void logic.
 * Rows with the same transactionId are grouped: Date/Time, TxnID, Match and Status
 * cells are merged (rowSpan) while Score, Odd, Bet and Void remain per-row.
 */
function BetDataRows({
  bets,
  allMatches,
  onCancelBet,
  currentUsername,
  currentUserId,
  hideUsernameCol,
  allVisibleBets = [],
  isCompanyViewer = false,
}: BetRowsSharedProps & { bets: BetRow[] }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [localCancelledIds, setLocalCancelledIds] = useState<Set<string>>(new Set());
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [passwordDialogId, setPasswordDialogId] = useState<string | null>(null);
  const [expiredIds, setExpiredIds] = useState<Set<string>>(new Set());
  const [viewReceiptTxnId, setViewReceiptTxnId] = useState<string | null>(null);

  const effectiveBets = useMemo(() =>
    bets.map(b => localCancelledIds.has(b.id) ? { ...b, status: BetStatus.CANCELLED } : b),
    [bets, localCancelledIds]
  );

  // Group effectiveBets by transactionId, preserving encounter order
  const txnGroups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, typeof effectiveBets>();
    effectiveBets.forEach(b => {
      const key = b.transactionId || b.id;
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key)!.push(b);
    });
    return order.map(k => map.get(k)!);
  }, [effectiveBets]);

  const handleVoidClick = useCallback((betId: string) => { setConfirmingId(betId); }, []);
  const handleTickConfirm = useCallback((betId: string) => { setConfirmingId(null); setPasswordDialogId(betId); }, []);
  const handleCrossCancel = useCallback((betId: string) => { if (confirmingId === betId) setConfirmingId(null); }, [confirmingId]);

  const handlePasswordConfirm = useCallback(async (betId: string, password: string) => {
    setCancellingId(betId);
    try {
      const { data, error } = await supabase.functions.invoke('auth_with_bcrypt_2026_03_26_08_35', {
        body: { username: currentUsername, password },
      });
      if (error || !data?.success) {
        alert(t.reports.incorrectPassword);
        setCancellingId(null);
        setPasswordDialogId(null);
        return;
      }
      setPasswordDialogId(null);
      // Mark this bet + all siblings with same transactionId as locally cancelled
      const voidedBet = bets.find(b => b.id === betId);
      const txnId = voidedBet?.transactionId;
      setLocalCancelledIds(prev => {
        const next = new Set(prev);
        next.add(betId);
        if (txnId) {
          // Also mark all sibling bets in the same transaction
          allVisibleBets.forEach(b => { if (b.transactionId === txnId) next.add(b.id); });
        }
        return next;
      });
      await onCancelBet(betId);
    } catch {
      alert('An error occurred. Please try again.');
    } finally {
      setCancellingId(null);
    }
  }, [onCancelBet, currentUsername, bets, allVisibleBets]);

  const handleExpire = useCallback((betId: string) => {
    setExpiredIds(prev => new Set(prev).add(betId));
    if (confirmingId === betId) setConfirmingId(null);
    if (passwordDialogId === betId) setPasswordDialogId(null);
  }, [confirmingId, passwordDialogId]);

  return (
    <>
      {passwordDialogId && (
        <PasswordConfirmDialog
          loading={cancellingId === passwordDialogId}
          onConfirm={(pw) => handlePasswordConfirm(passwordDialogId, pw)}
          onCancel={() => { setPasswordDialogId(null); setCancellingId(null); }}
        />
      )}
      <HistoryReceiptPortal
        viewReceiptTxnId={viewReceiptTxnId}
        allVisibleBets={allVisibleBets}
        effectiveBets={effectiveBets}
        allMatches={allMatches}
        onClose={() => setViewReceiptTxnId(null)}
      />
      {txnGroups.map(group => {
        const first = group[0];
        const span = group.length;
        const isCancelledGroup = group.every(b => b.status === BetStatus.CANCELLED);
        const match = allMatches.find(m => m.id === first.matchId);
        const statusKey = match ? getBetStatusKey(match.startTime, first.status) : 'Closed';
        const isPending = statusKey === 'Pending';
        const displayStatus = statusKey === 'Pending' ? t.reports.pending
          : isCancelledGroup ? t.reports.cancelled
          : t.reports.closed;
        const hasTxn = !!first.transactionId;
        const betAge = Date.now() - new Date(first.createdAt || '').getTime();
        const withinVoidWindow = betAge < VOID_WINDOW_MS && !expiredIds.has(first.id);
        const isOwnBet = currentUserId ? first.userId === currentUserId : first.username === currentUsername;
        // Company viewers can void any pending bet at any time (no 5-min window restriction)
        // Regular users can only void their own bets within the 5-min window
        const canVoid = isPending && !isCancelledGroup &&
          (isCompanyViewer || (withinVoidWindow && isOwnBet));
        // Re-Bet: data cells fade for cancelled groups; action cell stays fully opaque
        // Re-Bet: only for own cancelled bets, and only while betting is still open
        const bettingCutoff = match ? new Date(new Date(match.startTime).getTime() - 5 * 60 * 1000) : null;
        const bettingStillOpen = bettingCutoff ? new Date() < bettingCutoff : false;
        const canReBet = isCancelledGroup && isOwnBet && !!match && bettingStillOpen;

        return group.map((bet, idx) => {
          const isCancelled = bet.status === BetStatus.CANCELLED;
          const isFirst = idx === 0;
          // Apply faded style to data cells only, NOT the action cell,
          // so Re-Bet / Void buttons remain fully visible and clickable.
          const fadedCell = isCancelledGroup ? ' opacity-60' : '';
          return (
            <TableRow
              key={bet.id}
              className={`bg-white text-sm hover:bg-gray-50${isFirst ? ' border-t-2 border-t-gray-200' : ''}`}
            >
              {/* Merged: Username */}
              {isFirst && !hideUsernameCol && (
                <TableCell rowSpan={span} className={`align-middle pl-8 text-muted-foreground${fadedCell}`}>
                  {first.username}
                </TableCell>
              )}
              {/* Merged: Date & Time */}
              {isFirst && (
                <TableCell rowSpan={span} className={`align-middle font-mono text-xs${fadedCell}`}>
                  {fmtTs(first.createdAt)}
                </TableCell>
              )}
              {/* Merged: Transaction ID */}
              {isFirst && (
                <TableCell rowSpan={span} className={`align-middle font-mono text-[10px] text-muted-foreground select-all${fadedCell}`}>
                  {first.transactionId || '-'}
                </TableCell>
              )}
              {/* Merged: Match */}
              {isFirst && (
                <TableCell rowSpan={span} className={`align-middle text-xs${fadedCell}`}>
                  {first.matchName}
                </TableCell>
              )}
              {/* Per-bet: Score */}
              <TableCell className={`text-center font-mono font-semibold${fadedCell}`}>{bet.selection === 'Any Other Score' ? 'AOS' : (bet.selection || '-')}</TableCell>
              {/* Per-bet: Odd */}
              <TableCell className={`text-right font-mono${fadedCell}`}>{(bet.oddsAtTime || 0).toFixed(2)}</TableCell>
              {/* Per-bet: Bet Placed */}
              <TableCell className={`text-right font-mono${fadedCell}`}>{isCancelled ? 0 : bet.amount}</TableCell>
              {/* Per-bet: Fight % — company/agent viewer, shows viewer's own fight% against their direct child */}
              {isCompanyViewer && (
                <TableCell className={`text-right font-mono text-xs${fadedCell}`}>
                  {isCancelled ? '-' : `${bet.viewerFightPct ?? 0}%`}
                </TableCell>
              )}
              {/* Merged: Status + Receipt */}
              {isFirst && (
                <TableCell rowSpan={span} className={`align-middle text-center${fadedCell}`}>
                  <div className="flex flex-col items-center gap-1">
                    <Badge
                      variant={isPending ? 'outline' : 'secondary'}
                      className={
                        isPending ? 'text-yellow-600 border-yellow-400' :
                        isCancelledGroup ? 'text-red-500 border-red-400' :
                        'text-muted-foreground'
                      }
                    >
                      {displayStatus}
                    </Badge>
                    {hasTxn && (
                      <button
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 hover:bg-primary hover:text-white text-muted-foreground transition-colors"
                        title="View Receipt"
                        onClick={e => { e.stopPropagation(); setViewReceiptTxnId(first.transactionId!); }}
                      >
                        <Receipt className="h-3 w-3" />
                        {t.reports.receipt}
                      </button>
                    )}
                  </div>
                </TableCell>
              )}
              {/* Merged: Void + Re-Bet — always fully opaque so buttons are clearly clickable */}
              {isFirst && (
                <TableCell rowSpan={span} className="align-middle text-center min-w-[120px] opacity-100">
                  <div className="flex flex-col items-center gap-1">
                    {canVoid && (
                      confirmingId === first.id ? (
                        <div className="flex items-center gap-1 justify-center">
                          <Button variant="default" size="icon" className="h-6 w-6 bg-green-500 hover:bg-green-600 text-white border-0"
                            disabled={cancellingId === first.id}
                            onClick={e => { e.stopPropagation(); handleTickConfirm(first.id); }}>
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button variant="outline" size="icon" className="h-6 w-6"
                            onClick={e => { e.stopPropagation(); handleCrossCancel(first.id); }}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 justify-center">
                          <Button variant="outline" size="sm"
                            className="h-6 px-2 text-xs text-red-500 border-red-300 hover:bg-red-50 hover:text-red-700 hover:border-red-500"
                            disabled={cancellingId === first.id}
                            onClick={e => { e.stopPropagation(); handleVoidClick(first.id); }}>
                            {t.reports.void}
                          </Button>
                          <VoidCountdown
                            createdAt={first.createdAt || new Date().toISOString()}
                            onExpire={() => handleExpire(first.id)}
                          />
                        </div>
                      )
                    )}
                    {/* Re-Bet button: own cancelled bets only; disabled once betting closes */}
                    {isCancelledGroup && isOwnBet && match && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!canReBet}
                        title={canReBet ? 'Re-place these bets' : 'Betting is closed for this match'}
                        className={
                          canReBet
                            ? 'h-6 px-2 text-xs text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-800 hover:border-blue-500'
                            : 'h-6 px-2 text-xs text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                        }
                        onClick={e => {
                          e.stopPropagation();
                          if (!canReBet) return;
                          // Build bets payload: selection → amount string
                          const betMap: Record<string, string> = {};
                          group.forEach(b => {
                            if (b.selection && b.amount > 0) {
                              // Map "Any Other Score" → "AOS" for MatchBettingCard key compatibility
                              const key = b.selection === 'Any Other Score' ? 'AOS' : b.selection;
                              betMap[key] = String(b.amount);
                            }
                          });
                          sessionStorage.setItem('rebet_payload', JSON.stringify({
                            matchId: first.matchId,
                            bets: betMap,
                          }));
                          navigate(ROUTE_PATHS.MATCHES);
                        }}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        {t.reports.reBet}
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          );
        });
      })}
    </>
  );
}

/**
 * Helper: format a Date to DD/MM/YY HH:MM MYT for fight% band headers.
 * @deprecated — kept for potential future use
 */
function fmtFightBandTs(isoStr: string): string {
  try {
    const d = toMYT(isoStr);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear().toString().slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())} MYT`;
  } catch { return '-'; }
}

/**
 * Recursive hierarchy node for Agent/Company view.
 * Shows a collapsible header row, then recursively renders children.
 * For agent nodes that are a direct child of the viewer, bets are grouped
 * by the fight% applied at bet time (timestamp bands).
 */
function HierarchyBetNode({
  nodeUser,
  betsByUserId,
  allUsers,
  allMatches,
  onCancelBet,
  currentUsername,
  currentUserId,
  totalCols,
  depth,
  isCompanyViewer = false,
  showFightPctCol = false,
  viewerId,
}: {
  nodeUser: User;
  betsByUserId: Map<string, BetRow[]>;
  allUsers: User[];
  allMatches: Match[];
  onCancelBet: (betId: string) => Promise<void>;
  currentUsername: string;
  currentUserId?: string;
  totalCols: number;
  depth: number;
  isCompanyViewer?: boolean;
  /** Whether the Fight% column is shown (company-level viewer) */
  showFightPctCol?: boolean;
  /** The viewer's user id — used to decide whether to show fight% bands */
  viewerId?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { t } = useLanguage();

  // Collect all descendant bets (including players under sub-agents)
  const collectAllDescendantBets = (uid: string): BetRow[] => {
    const own = betsByUserId.get(uid) || [];
    const children = allUsers.filter(u => u.parentId === uid);
    return [...own, ...children.flatMap(c => collectAllDescendantBets(c.id))];
  };
  const allDescendantBets = useMemo(() => collectAllDescendantBets(nodeUser.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodeUser.id, betsByUserId, allUsers]);

  const ownBets = betsByUserId.get(nodeUser.id) || [];
  const childUsers = allUsers.filter(u => u.parentId === nodeUser.id);
  const hasContent = ownBets.length > 0 || childUsers.some(c => collectAllDescendantBets(c.id).length > 0);

  const indentPx = depth * 16;

  const bgClass = depth === 0
    ? 'bg-gray-100 hover:bg-gray-200'
    : depth === 1
    ? 'bg-gray-50 hover:bg-gray-100'
    : 'bg-white hover:bg-gray-50';

  // ── Fight% timestamp bands for direct-child nodes (agents AND players) ───────────────
  // Show bands when this node is a direct child of the viewer.
  // For agents: groups descendant player bets by the viewer's historical fight% against the agent.
  // For players: groups own bets by the viewer's fight% at bet time (fightPctSnapshot).
  // Available to both Agent-level and Company-level viewers.
  // viewerId is only passed by agent/company viewers, so its presence signals that viewer type.
  const isAgentOrCompanyViewer = viewerId != null;
  const isDirectChildOfViewer = viewerId != null && nodeUser.parentId === viewerId;
  // Show fight bands for direct-child nodes (agents OR players) when viewer is agent/company
  const showFightBands = isDirectChildOfViewer && isAgentOrCompanyViewer;

  // For agent nodes: collect all descendant PLAYER bets sorted by time
  // For player nodes: use own bets sorted by time
  const allBetsForBanding = useMemo(() => {
    if (!showFightBands) return [];
    const isPlayerNode = nodeUser.tier === UserTier.PLAYER;
    if (isPlayerNode) {
      // Player: band their own bets
      return [...(betsByUserId.get(nodeUser.id) || [])].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    }
    // Agent: collect all descendant player bets
    const collect = (uid: string): BetRow[] => {
      const betterUser = allUsers.find(u => u.id === uid);
      const isPlayer = betterUser?.tier === UserTier.PLAYER;
      const own = isPlayer ? (betsByUserId.get(uid) || []) : [];
      const children = allUsers.filter(u => u.parentId === uid);
      return [...own, ...children.flatMap(c => collect(c.id))];
    };
    return collect(nodeUser.id).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFightBands, nodeUser.id, nodeUser.tier, betsByUserId, allUsers]);

  // Group bets into bands by viewerFightPctAtTime
  // Only show multiple bands if there are actually 2+ different fight% values
  const fightBands = useMemo(() => {
    if (!showFightBands || allBetsForBanding.length === 0) return [];
    const raw: Array<{ pct: number; bets: BetRow[]; fromTs: string }> = [];
    for (const bet of allBetsForBanding) {
      const pct = bet.viewerFightPctAtTime ?? bet.viewerFightPct ?? 0;
      const last = raw[raw.length - 1];
      if (last && Math.abs(last.pct - pct) < 0.005) {
        last.bets.push(bet);
      } else {
        raw.push({ pct, bets: [bet], fromTs: bet.createdAt });
      }
    }
    // Return bands regardless of count — single band still shows fight% header
    return raw;
  }, [showFightBands, allBetsForBanding]);

  if (allDescendantBets.length === 0) return null;

  return (
    <>
      {/* Header row for this node */}
      <TableRow
        className={`cursor-pointer font-semibold ${bgClass}`}
        onClick={() => setIsExpanded(e => !e)}
      >
        <TableCell colSpan={totalCols}>
          <div className="flex items-center gap-1" style={{ paddingLeft: `${indentPx}px` }}>
            {hasContent && (
              isExpanded
                ? <ChevronDown className="h-3 w-3 shrink-0" />
                : <ChevronRight className="h-3 w-3 shrink-0" />
            )}
            <span>{nodeUser.username}</span>
            {/* Tier badge */}
            {nodeUser.tier === UserTier.AGENT && (
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-100 text-blue-700 border border-blue-300">{t.reports.tierAgent}</span>
            )}
            {nodeUser.tier === UserTier.PLAYER && (
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700 border border-green-300">{t.reports.tierPlayer}</span>
            )}
            {nodeUser.tier === UserTier.COMPANY && (
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-100 text-purple-700 border border-purple-300">{t.reports.tierCompany}</span>
            )}
            {/* Fight% badge removed per user request */}
            <Badge variant="outline" className="ml-1 text-[10px] px-1">{allDescendantBets.length}</Badge>
          </div>
        </TableCell>
      </TableRow>

      {/* Expanded content */}
      {isExpanded && (
        <>
          {/* Own bets — only for PLAYER nodes */}
          {nodeUser.tier === UserTier.PLAYER && ownBets.length > 0 && (
            <BetDataRows
              bets={ownBets}
              allMatches={allMatches}
              onCancelBet={onCancelBet}
              currentUsername={currentUsername}
              currentUserId={currentUserId}
              hideUsernameCol={false}
              totalCols={totalCols}
              allVisibleBets={Array.from(betsByUserId.values()).flat()}
              isCompanyViewer={isCompanyViewer}
            />
          )}
          {/* Child nodes recursively */}
          {childUsers.map(child => (
            <HierarchyBetNode
              key={child.id}
              nodeUser={child}
              betsByUserId={betsByUserId}
              allUsers={allUsers}
              allMatches={allMatches}
              onCancelBet={onCancelBet}
              currentUsername={currentUsername}
              currentUserId={currentUserId}
              totalCols={totalCols}
              depth={depth + 1}
              isCompanyViewer={isCompanyViewer}
              showFightPctCol={showFightPctCol}
              viewerId={viewerId}
            />
          ))}
        </>
      )}
    </>
  );
}

/**
 * CompanyAgentExpandableNode — shown in the company report's agent list view.
 * Renders a single row per direct child agent. The agent username is a clickable
 * link that navigates to the agent's full dedicated report view.
 */
function CompanyAgentExpandableNode({
  agent,
  allUsers,
  allBets,
  totalCols,
  onViewFullReport,
}: {
  agent: User;
  allUsers: User[];
  allBets: Bet[];
  totalCols: number;
  onViewFullReport: () => void;
}) {
  // Count all bets under this agent for the badge
  const agentDescIds = useMemo(
    () => new Set([agent.id, ...getDescendantIds(agent.id, allUsers)]),
    [agent.id, allUsers]
  );
  const agentBetCount = useMemo(
    () => allBets.filter(b => agentDescIds.has(b.userId)).length,
    [allBets, agentDescIds]
  );

  return (
    <React.Fragment>
      {/* Agent header row — username is a link to full report */}
      <TableRow className="font-semibold bg-gray-100 hover:bg-gray-200 border-b">
        <TableCell colSpan={totalCols}>
          <div className="flex items-center gap-2 py-0.5">
            {/* Username as clickable link → full agent report */}
            <button
              className="font-semibold text-sm text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors"
              onClick={() => onViewFullReport()}
            >
              {agent.username}
            </button>
            {agent.nickname && agent.nickname !== agent.username && (
              <span className="text-xs text-muted-foreground">({agent.nickname})</span>
            )}
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-100 text-blue-700 border border-blue-300">
              Agent
            </span>
            <Badge variant="outline" className="ml-1 text-[10px] px-1">{agentBetCount}</Badge>
          </div>
        </TableCell>
      </TableRow>

    </React.Fragment>
  );
}

/** Main Bet History panel */
export function ReportsPanel() {
  const { user, patchUser } = useAuth();
  const { bets: allBets, matches: allMatches, cancelBet } = useBetting();

  // Wrap cancelBet to instantly patch credit in auth state after void
  const handleCancelBet = useCallback(async (betId: string) => {
    const result = await cancelBet(betId);
    // result.newCreditByUser is a map of userId -> new available credit
    if (result?.newCreditByUser && user?.id && result.newCreditByUser[user.id] !== undefined) {
      patchUser({ availableCreditLimit: result.newCreditByUser[user.id] });
    }
  }, [cancelBet, patchUser, user?.id]);
  const { users: allUsers } = useUserManagement();
  const { t } = useLanguage();

  // Draft values — only committed to applied on Search click
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  // Applied (committed) values — drive the actual filtering
  const [appliedFrom, setAppliedFrom] = useState<string>('');
  const [appliedTo, setAppliedTo] = useState<string>('');
  const [selectedMatchId, setSelectedMatchId] = useState<string>('all');

  const handleSearch = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setSelectedMatchId('all');
  };

  const handleClear = () => {
    setDateFrom('');
    setDateTo('');
    setAppliedFrom('');
    setAppliedTo('');
    setSelectedMatchId('all');
  };

  const isPlayer = user?.tier === UserTier.PLAYER;
  const isAgentOrCompany = user?.tier === UserTier.AGENT || user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER;
  const showFightPctCol = user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER;

  // Player has no Username column → 8 cols; others have 9; company adds Fight% → 10
  const totalCols = isPlayer ? 8 : showFightPctCol ? 10 : 9;

  const filteredMatches = useMemo(() => {
    if (!appliedFrom && !appliedTo) return allMatches;
    return allMatches.filter(m => {
      // Use MYT date so early-morning MYT matches (e.g. 3 AM MYT = previous UTC day) are correctly bucketed
      const matchDate = format(toMYT(m.startTime), 'yyyy-MM-dd');
      if (appliedFrom && matchDate < appliedFrom) return false;
      if (appliedTo && matchDate > appliedTo) return false;
      return true;
    });
  }, [allMatches, appliedFrom, appliedTo]);

  /** All bets visible to the current user (self + all descendants), filtered */
  const visibleBets = useMemo((): BetRow[] => {
    if (!user) return [];
    const allowedIds = new Set([user.id, ...getDescendantIds(user.id, allUsers)]);
    return allBets
      .filter(b => {
        if (!allowedIds.has(b.userId)) return false;
        // For agent/company viewers only show player bets (agents don't place bets)
        if (isAgentOrCompany) {
          const betUser = allUsers.find(u => u.id === b.userId);
          if (betUser && betUser.tier !== UserTier.PLAYER) return false;
        }
        const match = allMatches.find(m => m.id === b.matchId);
        if (!match) return false;
        // Use MYT date so early-morning MYT matches are correctly bucketed
        const matchDate = format(toMYT(match.startTime), 'yyyy-MM-dd');
        if (appliedFrom && matchDate < appliedFrom) return false;
        if (appliedTo && matchDate > appliedTo) return false;
        if (selectedMatchId !== 'all' && b.matchId !== selectedMatchId) return false;
        return true;
      })
      .map(b => {
        const betUser = allUsers.find(u => u.id === b.userId);
        const match = allMatches.find(m => m.id === b.matchId);
        return {
          ...b,
          username: betUser?.nickname || betUser?.username || 'Unknown',
          matchName: match ? `${match.matchNumber ? match.matchNumber + ' - ' : ''}${match.homeTeam} vs ${match.awayTeam}` : 'Unknown Match',
          ticketPrice: betUser?.ticketPrice ?? b.ticketPrice ?? 0,
          fightPercentage: betUser?.fightPercentage ?? b.fightPercentage ?? 0,
        };
      });
  }, [user, allBets, allUsers, allMatches, appliedFrom, appliedTo, selectedMatchId, isAgentOrCompany]);

  /** Group filtered bets by userId for hierarchy rendering */
  const betsByUserId = useMemo(() => {
    const map = new Map<string, BetRow[]>();
    visibleBets.forEach(b => {
      const arr = map.get(b.userId) || [];
      arr.push(b);
      map.set(b.userId, arr);
    });
    return map;
  }, [visibleBets]);

  /** Direct children of the current user — top-level nodes for hierarchy view */
  const directChildren = useMemo(() => {
    if (!user || isPlayer) return [];
    return allUsers.filter(u => u.parentId === user.id);
  }, [user, isPlayer, allUsers]);

  if (!user) return null;

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <BookOpen className="w-5 h-5 text-primary" />
        <h1 className="text-xl font-bold tracking-tight">{t.reports.pageTitle}</h1>
        <Badge variant="outline" className="capitalize">{user.tier.toLowerCase()}</Badge>
      </div>

      {/* Filters */}
      <Card className="border-border/40">
        <CardContent className="px-4 py-3">
          <div className="space-y-2">
            {/* Title row */}
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-xs font-semibold text-foreground">{t.reports.matchDateRange}</span>
              {(appliedFrom || appliedTo) && (
                <button
                  onClick={handleClear}
                  className="ml-auto text-[10px] px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
                >
                  ✕ Clear
                </button>
              )}
            </div>

            {/* From → To + Search button on one row */}
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-0.5">
                <label className="text-[10px] text-muted-foreground">{t.reports.from}</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="h-8 text-sm"
                />
              </div>
              <span className="text-muted-foreground font-bold pb-1.5 shrink-0">→</span>
              <div className="flex-1 space-y-0.5">
                <label className="text-[10px] text-muted-foreground">{t.reports.to}</label>
                <Input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={e => setDateTo(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="h-8 text-sm"
                />
              </div>
              <Button
                onClick={handleSearch}
                size="sm"
                className="h-8 px-3 shrink-0 text-xs font-semibold"
              >
                🔍 Search
              </Button>
            </div>

            {/* Match dropdown — scoped to applied date range */}
            <div className="space-y-0.5">
              <label className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Filter className="w-3 h-3" /> Match
                {(appliedFrom || appliedTo) && filteredMatches.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] font-semibold">
                    {filteredMatches.length} match{filteredMatches.length !== 1 ? 'es' : ''}
                  </span>
                )}
              </label>
              <Select value={selectedMatchId} onValueChange={setSelectedMatchId}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="All Matches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.reports.allMatches}</SelectItem>
                  {filteredMatches.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {format(toMYT(m.startTime), 'dd MMM yyyy HH:mm')} MYT · {m.homeTeam} vs {m.awayTeam}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Active filter summary — only shown after Search is applied */}
            {(appliedFrom || appliedTo) && (
              <div className="flex items-center gap-1.5 pt-0.5">
                <span className="text-[10px] text-muted-foreground">{t.reports.showingDates}</span>
                <span className="text-[10px] font-semibold text-primary">
                  {appliedFrom ? format(new Date(appliedFrom), 'dd MMM yyyy') : '—'}
                  {' → '}
                  {appliedTo ? format(new Date(appliedTo), 'dd MMM yyyy') : '—'}
                </span>
                <span className="text-[10px] text-muted-foreground">·</span>
                <span className="text-[10px] font-semibold">{visibleBets.length} bet{visibleBets.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden text-[6pt] lg:text-base bg-white">
        <Table>
          <TableHeader className="bg-gray-100">
            <TableRow>
              {!isPlayer && <TableHead className="font-semibold">{t.reports.username}</TableHead>}
              <TableHead className="font-semibold">{t.reports.dateTime}</TableHead>
              <TableHead className="font-semibold">{t.reports.transactionId}</TableHead>
              <TableHead className="font-semibold">{t.reports.match}</TableHead>
              <TableHead className="text-center font-semibold">{t.reports.score}</TableHead>
              <TableHead className="text-right font-semibold">{t.reports.odd}</TableHead>
              <TableHead className="text-right font-semibold">{t.reports.betPlaced}</TableHead>
              {showFightPctCol && <TableHead className="text-right font-semibold">Fight %</TableHead>}
              <TableHead className="text-center font-semibold">{t.reports.statusCol}</TableHead>
              <TableHead className="w-28" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleBets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalCols} className="text-center py-10 text-muted-foreground">
                  {t.reports.noBets}
                </TableCell>
              </TableRow>
            ) : isPlayer ? (
              /* ── Player: flat list of own bets (no hierarchy) ── */
              <BetDataRows
                bets={visibleBets}
                allMatches={allMatches}
                onCancelBet={handleCancelBet}
                currentUsername={user.username}
                currentUserId={user.id}
                hideUsernameCol={true}
                totalCols={totalCols}
                allVisibleBets={visibleBets}
              />
            ) : (
              /* ── Agent / Company: collapsed hierarchy rows per direct child ── */
              directChildren.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalCols} className="text-center py-10 text-muted-foreground">
                    {t.reports.noBets}
                  </TableCell>
                </TableRow>
              ) : (
                directChildren.map(child => (
                  <HierarchyBetNode
                    key={child.id}
                    nodeUser={child}
                    betsByUserId={betsByUserId}
                    allUsers={allUsers}
                    allMatches={allMatches}
                    onCancelBet={handleCancelBet}
                    currentUsername={user.username}
                    currentUserId={user.id}
                    totalCols={totalCols}
                    depth={0}
                    isCompanyViewer={showFightPctCol}
                    showFightPctCol={showFightPctCol}
                    viewerId={user.id}
                  />
                ))
              )
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export { ReportsPanel as ReportTable };
export { ReportsPanel as ReportFilters };
export { ReportsPanel as UserHierarchySelector };
